import { useEffect } from 'react';

/**
 * Confirm before leaving a page whose in-progress work would be lost.
 *
 * A mock interview holds state that only exists in the tab: the transcript, the
 * queued narration, the stage the candidate has reached. Leaving mid-interview
 * discards all of it, and the three ways people actually leave need three
 * different mechanisms.
 *
 * `useBlocker` would handle the in-app case on its own, but it requires a data
 * router (`createBrowserRouter` + `RouterProvider`). This app mounts a plain
 * `<BrowserRouter>` in index.js with every route nested under `/*` in App.js,
 * so `useBlocker` throws here. Migrating the router for one confirmation dialog
 * is a much larger change than intercepting the three exits directly.
 *
 * Covered:
 *
 *   1. Tab close, reload, typing a new URL -> `beforeunload`. The browser shows
 *      its own wording; `message` cannot change it. Chrome also ignores this
 *      entirely unless the user has interacted with the page, which in an
 *      interview they always have.
 *   2. Clicking an in-app link -> a capture-phase click listener. The Navbar
 *      renders during interviews (App.js:106), so this is the most likely exit,
 *      and `<Link>` renders a plain anchor that this can intercept.
 *   3. Browser Back -> `popstate`, guarded by a sentinel history entry.
 *
 * Uses `window.confirm` deliberately. It is synchronous, which is what lets the
 * click handler decide whether to `preventDefault` and lets the popstate
 * handler re-arm before the browser settles. An in-app modal would be prettier
 * but needs pending-navigation state threaded through all three paths, and
 * `beforeunload` is a native dialog regardless -- so a custom modal would make
 * the experience less consistent, not more.
 *
 * @param {Object} options
 * @param {boolean} options.when - guard only while this is true
 * @param {string} options.message - shown for in-app navigation and Back
 */
const useNavigationGuard = ({ when, message }) => {
  // 1. Tab close / reload / external navigation.
  useEffect(() => {
    if (!when) return undefined;

    const handleBeforeUnload = (event) => {
      // Both are required: `preventDefault` for the modern spec, `returnValue`
      // for older Chrome and Safari, which ignore the former.
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [when]);

  // 2. In-app navigation via an anchor (react-router <Link> renders one).
  useEffect(() => {
    if (!when) return undefined;

    const handleClick = (event) => {
      // Let the user's own modifier-click open a new tab; that leaves the
      // interview running in this one, so there is nothing to confirm.
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const anchor = event.target?.closest?.('a[href]');
      if (!anchor) return;
      if (anchor.target && anchor.target !== '_self') return;
      if (anchor.hasAttribute('download')) return;

      const href = anchor.getAttribute('href');
      if (!href) return;
      // In-page jumps and non-navigating protocols leave the interview running.
      if (/^(#|mailto:|tel:|javascript:)/i.test(href)) return;

      const destination = new URL(anchor.href, window.location.href);
      // Another origin leaves the SPA entirely; beforeunload covers that one.
      if (destination.origin !== window.location.origin) return;
      // Same page: nothing is lost, so do not nag.
      if (
        destination.pathname === window.location.pathname &&
        destination.search === window.location.search
      ) {
        return;
      }

      if (!window.confirm(message)) {
        // Capture phase plus stopPropagation: react-router's own handler is
        // attached to the anchor, so stopping here is what prevents the
        // client-side navigation from running at all.
        event.preventDefault();
        event.stopPropagation();
      }
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [when, message]);

  // 3. Browser Back.
  useEffect(() => {
    if (!when) return undefined;

    // A sentinel entry duplicating the current URL, so the first Back lands
    // back here and fires popstate rather than leaving the page outright.
    window.history.pushState(null, '', window.location.href);

    const handlePopState = () => {
      if (window.confirm(message)) {
        // Approved: step back past the entry the sentinel was covering. The
        // listener is removed first so this second Back is not itself caught.
        window.removeEventListener('popstate', handlePopState);
        window.history.back();
        return;
      }
      // Declined: re-arm, so the next Back asks again.
      window.history.pushState(null, '', window.location.href);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [when, message]);
};

export default useNavigationGuard;
